import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Slider,
  Switch,
  Typography,
} from "@mui/material";
import { useAlert } from "../../../../context/AlertContext";
import { useTranslation } from "react-i18next";
import { useLoginStatus } from "../../../../context/LoginStatusContext";
import { apiRequest } from "../../../../utils/api";

interface OrganizationSettings {
  tenak_ai: {
    mind_map_enabled: boolean;
    browsing_enabled: boolean;
    color_palette_enabled: boolean;
    prompt_template_enabled: boolean;
    writing_ai_enabled: boolean;
  };
  studio_juno: {
    image_generation_enabled: boolean;
    video_generation_enabled: boolean;
    sound_generation_enabled: boolean;
    image_generation_settings: {
      safety_level: number;
    };
  };
  notes_nebula: {
    article_publishing_enabled: boolean;
  };
}

const OrganizationSettings = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useLoginStatus();
  const { setAlert } = useAlert();
  const [settings, setSettings] = useState<OrganizationSettings>({
    tenak_ai: {
      mind_map_enabled: true,
      browsing_enabled: true,
      color_palette_enabled: true,
      prompt_template_enabled: true,
      writing_ai_enabled: true,
    },
    studio_juno: {
      image_generation_enabled: true,
      video_generation_enabled: true,
      sound_generation_enabled: true,
      image_generation_settings: {
        safety_level: 1.5,
      },
    },
    notes_nebula: {
      article_publishing_enabled: true,
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modified, setModified] = useState(false);

  const organizationUuid = user?.enterprise?.organization?.uuid;
  const userRole = user?.enterprise?.role;
  const permissions = user?.enterprise?.permissions || [];

  const isOwner = userRole === "owner";
  const hasFullEditPermission = isOwner || permissions.includes("edit_organization_settings");
  const hasPartialEditPermission = permissions.includes("edit_partial_settings");
  const canEdit = hasFullEditPermission || hasPartialEditPermission;

  useEffect(() => {
    if (organizationUuid) {
      fetchSettings();
    }
  }, [organizationUuid]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/api/v1/enterprise/organizations/${organizationUuid}/settings/`, "GET");

      // 既存の設定がある場合は使用し、なければデフォルト値を維持
      if (response.data) {
        const orgSettings = { ...settings };

        // Tenak AI設定
        if (response.data.tenak_ai) {
          orgSettings.tenak_ai = {
            ...orgSettings.tenak_ai,
            ...response.data.tenak_ai,
          };
        }

        // Studio Juno設定
        if (response.data.studio_juno) {
          orgSettings.studio_juno = {
            ...orgSettings.studio_juno,
            ...response.data.studio_juno,
          };

          // 画像生成の安全性レベル
          if (response.data.studio_juno.image_generation_settings) {
            orgSettings.studio_juno.image_generation_settings = {
              ...orgSettings.studio_juno.image_generation_settings,
              ...response.data.studio_juno.image_generation_settings,
            };
          }
        }

        // Notes Nebula設定
        if (response.data.notes_nebula) {
          orgSettings.notes_nebula = {
            ...orgSettings.notes_nebula,
            ...response.data.notes_nebula,
          };
        }

        setSettings(orgSettings);
      }

      setLoading(false);
    } catch (error) {
      console.error("設定取得エラー:", error);
      setAlert("error", t("preferences.enterprise.error.fetchSettings"));
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);

      await apiRequest(`/api/v1/enterprise/organizations/${organizationUuid}/settings/`, "PATCH", settings);

      setAlert("success", t("preferences.enterprise.success.saveSettings"));
      setModified(false);
      setSaving(false);
      refreshUser();
    } catch (error) {
      console.error("設定保存エラー:", error);
      setAlert("error", t("preferences.enterprise.error.saveSettings"));
      setSaving(false);
    }
  };

  // Tenak AI設定の変更ハンドラ
  const handleTenakAIChange =
    (setting: keyof typeof settings.tenak_ai) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setSettings({
        ...settings,
        tenak_ai: {
          ...settings.tenak_ai,
          [setting]: event.target.checked,
        },
      });
      setModified(true);
    };

  // Studio Juno設定の変更ハンドラ
  const handleStudioJunoChange =
    (setting: keyof Omit<typeof settings.studio_juno, "image_generation_settings">) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSettings({
        ...settings,
        studio_juno: {
          ...settings.studio_juno,
          [setting]: event.target.checked,
        },
      });
      setModified(true);
    };

  // 画像安全性レベルの変更ハンドラ
  const handleSafetyLevelChange = (event: Event, newValue: number | number[]) => {
    setSettings({
      ...settings,
      studio_juno: {
        ...settings.studio_juno,
        image_generation_settings: {
          ...settings.studio_juno.image_generation_settings,
          safety_level: newValue as number,
        },
      },
    });
    setModified(true);
  };

  // Notes Nebula設定の変更ハンドラ
  const handleNotesNebulaChange =
    (setting: keyof typeof settings.notes_nebula) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setSettings({
        ...settings,
        notes_nebula: {
          ...settings.notes_nebula,
          [setting]: event.target.checked,
        },
      });
      setModified(true);
    };

  if (!user?.enterprise) {
    return <Typography>{t("preferences.enterprise.noEnterpriseInfo")}</Typography>;
  }

  if (loading) {
    return <Typography>{t("preferences.enterprise.loading")}</Typography>;
  }

  return (
    <Box>
      {!canEdit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("preferences.enterprise.readOnlyMode")}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* Tenak AI設定 */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Tenak AI
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("preferences.enterprise.organizationFeatures")}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.tenak_ai.mind_map_enabled}
                      onChange={handleTenakAIChange("mind_map_enabled")}
                      disabled={
                        !(
                          hasFullEditPermission ||
                          (hasPartialEditPermission && permissions.includes("edit_tenak_mind_map"))
                        )
                      }
                    />
                  }
                  label={t("preferences.enterprise.mindMapEnabled")}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.tenak_ai.browsing_enabled}
                      onChange={handleTenakAIChange("browsing_enabled")}
                      disabled={
                        !(
                          hasFullEditPermission ||
                          (hasPartialEditPermission && permissions.includes("edit_tenak_browsing"))
                        )
                      }
                    />
                  }
                  label={t("preferences.enterprise.browsingEnabled")}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.tenak_ai.color_palette_enabled}
                      onChange={handleTenakAIChange("color_palette_enabled")}
                      disabled={
                        !(
                          hasFullEditPermission ||
                          (hasPartialEditPermission && permissions.includes("edit_tenak_color_palette"))
                        )
                      }
                    />
                  }
                  label={t("preferences.enterprise.colorPaletteEnabled")}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.tenak_ai.prompt_template_enabled}
                      onChange={handleTenakAIChange("prompt_template_enabled")}
                      disabled={
                        !(
                          hasFullEditPermission ||
                          (hasPartialEditPermission && permissions.includes("edit_tenak_prompt_template"))
                        )
                      }
                    />
                  }
                  label={t("preferences.enterprise.promptTemplateEnabled")}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.tenak_ai.writing_ai_enabled}
                      onChange={handleTenakAIChange("writing_ai_enabled")}
                      disabled={
                        !(
                          hasFullEditPermission ||
                          (hasPartialEditPermission && permissions.includes("edit_tenak_writing_ai"))
                        )
                      }
                    />
                  }
                  label={t("preferences.enterprise.writingAIEnabled")}
                />
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Studio Juno設定 */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Studio Juno
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("preferences.enterprise.organizationFeatures")}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.studio_juno.image_generation_enabled}
                      onChange={handleStudioJunoChange("image_generation_enabled")}
                      disabled={
                        !(
                          hasFullEditPermission ||
                          (hasPartialEditPermission && permissions.includes("edit_juno_image_generation"))
                        )
                      }
                    />
                  }
                  label={t("preferences.enterprise.imageGenerationEnabled")}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.studio_juno.video_generation_enabled}
                      onChange={handleStudioJunoChange("video_generation_enabled")}
                      disabled={
                        !(
                          hasFullEditPermission ||
                          (hasPartialEditPermission && permissions.includes("edit_juno_video_generation"))
                        )
                      }
                    />
                  }
                  label={t("preferences.enterprise.videoGenerationEnabled")}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.studio_juno.sound_generation_enabled}
                      onChange={handleStudioJunoChange("sound_generation_enabled")}
                      disabled={
                        !(
                          hasFullEditPermission ||
                          (hasPartialEditPermission && permissions.includes("edit_juno_sound_generation"))
                        )
                      }
                    />
                  }
                  label={t("preferences.enterprise.soundGenerationEnabled")}
                />
              </FormGroup>

              <Divider sx={{ my: 2 }} />

              <Typography gutterBottom>{t("preferences.enterprise.imageSafetyLevel")}</Typography>
              <Box sx={{ px: 2 }}>
                <Slider
                  value={settings.studio_juno.image_generation_settings.safety_level}
                  onChange={handleSafetyLevelChange}
                  min={1.1}
                  max={3}
                  step={0.1}
                  marks={[
                    { value: 1.1, label: "Safe" },
                    { value: 1.5, label: "Default" },
                    { value: 2.0, label: "Unsafe" },
                    { value: 3.0, label: "No Filter" },
                  ]}
                  valueLabelDisplay="auto"
                  disabled={
                    !(
                      hasFullEditPermission ||
                      (hasPartialEditPermission && permissions.includes("edit_juno_safety_level"))
                    )
                  }
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {t("preferences.enterprise.organizationSettingsOverride")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes Nebula設定 */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                Notes Nebula
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("preferences.enterprise.organizationFeatures")}
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notes_nebula.article_publishing_enabled}
                      onChange={handleNotesNebulaChange("article_publishing_enabled")}
                      disabled={
                        !(
                          hasFullEditPermission ||
                          (hasPartialEditPermission && permissions.includes("edit_nebula_article_publishing"))
                        )
                      }
                    />
                  }
                  label={t("preferences.enterprise.articlePublishingEnabled")}
                />
              </FormGroup>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {canEdit && (
        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveSettings}
            disabled={saving || !modified}
            disableElevation
          >
            {saving ? t("preferences.enterprise.saving") : t("preferences.enterprise.saveSettings")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default OrganizationSettings;
